/** 审核单状态（与 audit-service review_task.status 对应） */
export enum ReviewStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
}

/** 审核单业务类型（与 audit-service biz_type 对应） */
export enum ReviewBizType {
    CUSTOMER_COMPLAINT = 'CUSTOMER_COMPLAINT',
}

/** 审核操作（与 audit-service review_task_log.action 对应） */
export enum ReviewAction {
    SUBMIT = 'SUBMIT',
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
    CANCEL = 'CANCEL',
}

/** 审核结果（业务侧 customer_complaint_review.review_result） */
export enum ReviewResult {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
}

/** 工作台 Tab（前端 UI 概念，映射到 ReviewStatus + 一个"全部"虚拟值） */
export enum ReviewTaskTab {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    ALL = 'ALL',
}

/** 审核单列表项（对应 ReviewTaskType GraphQL Type） */
export interface ReviewTaskListItem {
    reviewTaskId: number;
    bizType: string;
    bizId: number;
    status: string;
    submittedBy?: number;
    submitterName?: string;
    submitterPhone?: string;
    /** epoch seconds */
    submittedAt?: number;
    /** epoch seconds */
    slaDueAt?: number;
    reviewerId?: number;
    reviewerName?: string;
    /** epoch seconds */
    reviewedAt?: number;
    trackingNumber?: string;
    orderId?: number;
    dspId?: number;
    dspName?: string;
    driverId?: number;
    driverName?: string;
    /** 二级业务分类 code（按 bizType 路由：CUSTOMER_COMPLAINT→customer_complaint.complaint_type） */
    bizSubType?: string;
}

/** 审核单详情（含操作日志 + 业务详情 + 订单详情） */
export interface ReviewTaskDetail {
    reviewTask: ReviewTaskListItem;
    logs: ReviewTaskLog[];
    complaint?: CustomerComplaintDetail;
    complaintAppeal?: ComplaintAppealDetail;
    orderDetail?: ReviewTaskOrderDetail;
}

/** 审核工作台展示的订单详情（5 字段） */
export interface ReviewTaskOrderDetail {
    overallStatus?: string;
    recipientName?: string;
    deliveryAddress?: string;
    /** epoch seconds */
    deliveredAt?: number;
    driverName?: string;
    /** POD 图 URL 列表（后端按 key 拼预签名 URL） */
    podImageS3UrlsJson?: string[];
}

/** 审核工作台筛选条件（对应后端 ReviewTaskFiltersType） */
export interface ReviewTaskFiltersTypeInput {
    bizType: string;
    statuses?: string[] | null;
    reviewTaskId?: number | null;
    trackingNumbers?: string | null;
    dspId?: number | null;
    /** 二级业务分类 code */
    subBizType?: string | null;
    submittedAtFrom?: string | null;
    submittedAtTo?: string | null;
    /** 审核完成时间区间起（含）；仅对已审核工单生效 */
    reviewedAtFrom?: string | null;
    /** 审核完成时间区间止（不含） */
    reviewedAtTo?: string | null;
}

/** 通用分页请求（对应后端 PageRequestType） */
export interface PageRequestTypeInput {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
}

export interface ReviewTaskLog {
    id: number;
    reviewTaskId: number;
    action: string;
    fromStatus?: string;
    toStatus: string;
    operatorId: number;
    operatorName?: string;
    comment?: string;
    /** epoch seconds */
    createdAt?: number;
}

export interface AppealReasonParsed {
    text: string;
    lang: string;
    translations: Array<{lang: string; text: string}>;
}

/** 客诉申诉详情（来自业务侧 customer_complaint_review，瘦身后只保留申诉内容+审核结果） */
export interface ComplaintAppealDetail {
    id: number;
    complaintId: number;
    reviewTaskId?: number;
    /** 后端结构化解析的申诉理由；前端按 i18n.language 选 translations[locale] ?? text */
    appealReason?: AppealReasonParsed | null;
    /** 后端解析 S3 key 后拼好的预签名 URL 列表，前端直接渲染 */
    appealAttachmentS3UrlsJson?: string[];
    /** epoch seconds */
    appealSubmittedAt?: number;
    appealSubmittedBy?: number;
    reviewResult?: string;
    reviewerId?: number;
    /** 后端结构化解析的审核评论，前端无需自行 JSON.parse；入库 JSON 为空/解析失败时为 null */
    reviewComment?: ReviewCommentParsed | null;
    /** epoch seconds */
    reviewedAt?: number;
}

export interface ReviewCommentTranslationEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    /** 该语言下的翻译文本 */
    text: string;
}

export interface ReviewRejectReasonOptionInline {
    /** 字典 code，与 review_comment_json.tagKeys 中的值一致 */
    code: string;
    /** 展示顺序 */
    displayOrder?: number;
    /** 可扩展多语言文案，按 locale 取 */
    texts: {lang: string; text: string}[];
}

export interface ReviewCommentParsed {
    /** 标签 code 列表（与 ReviewRejectReasonOption.code 对齐；用于表单反填与提交序列化） */
    tagKeys: string[];
    /** 按 tagKeys 顺序解析后的字典项；前端展示直接按 locale 从 texts 取 */
    tagOptions: ReviewRejectReasonOptionInline[];
    /** 自由文本原文 */
    text: string;
    /** 自由文本源语言 */
    lang: string;
    /** 自由文本翻译列表 */
    translations: ReviewCommentTranslationEntry[];
}

export interface ComplaintReasonTranslationEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    /** 该语言下的翻译文本 */
    text: string;
}

/** 客诉原因结构化解析结果，后端 swiftx-delivery-service 服务端解析后透传 */
export interface ComplaintReasonParsed {
    /** 原文 */
    text: string;
    /** 原文源语言：zh|en|es */
    lang: string;
    /** 翻译列表 */
    translations: ComplaintReasonTranslationEntry[];
}

/** 客诉主表详情（来自业务侧 customer_complaint，单条查询时再拉一次） */
export interface CustomerComplaintDetail {
    id: number;
    complaintCategory: string;
    complaintType: string;
    /** 客诉发起渠道：EMAIL/PHONE/OTHER；用于轨迹"下发"节点区分客服 vs 系统 */
    source?: string;
    externalRefId?: string;
    externalRefCode?: string;
    driverId: number | null;
    dspId: number | null;
    orderId?: number;
    hubId?: number;
    warehouseId?: number;
    /** 客诉原因结构化对象（含原文+三语译文）；JSON 解析已下沉到后端，前端直接消费 */
    complaintReason?: ComplaintReasonParsed | null;
    /** 证据文件列表JSON：[{"file_name":"xxx.jpg","file_type":"jpg","file_url":"https://..."}] */
    evidenceFilesJson?: string;
    status: string;
    /** 完结态标志：0=进行中；1=已完结 */
    isFinished: number;
    /** DSP 申诉/处理截止时间（epoch seconds） */
    appealDeadlineAt?: number;
    /** 完结时间（epoch seconds；任意完结态发生时刻；通过 status 区分类型：REVOKED/ENFORCED/ACKNOWLEDGED/TIMEOUT/CLOSED） */
    finishedAt?: number;
    /** epoch seconds */
    createdAt?: number;
    /** epoch seconds */
    updatedAt?: number;
}

/** 列表分页结果 */
export interface ReviewTaskListPage {
    content: ReviewTaskListItem[];
    totalElements: number;
    page: number;
    size: number;
}

/** Tab → 状态映射 */
export const TAB_TO_STATUS: Record<ReviewTaskTab, string | undefined> = {
    [ReviewTaskTab.PENDING]: ReviewStatus.PENDING_REVIEW,
    [ReviewTaskTab.APPROVED]: ReviewStatus.APPROVED,
    [ReviewTaskTab.REJECTED]: ReviewStatus.REJECTED,
    [ReviewTaskTab.CANCELLED]: ReviewStatus.CANCELLED,
    [ReviewTaskTab.ALL]: undefined, // ALL 不走 status 过滤
};
